(() => {
  if (customElements.get('cp-quantity-breaks')) return;

  class CpQuantityBreaks extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      this.variantInput = this.form ? this.form.querySelector('[name="id"]') : null;
      this.quantityInput = this.form ? this.form.querySelector('[name="quantity"]') : null;
      this.variants = this.parseVariants();
      this.boundOnChange = this.onChange.bind(this);
      this.boundOnClick = this.onClick.bind(this);
      this.boundOnDocumentClick = this.onDocumentClick.bind(this);
      this.boundOnFormSubmit = this.onFormSubmit.bind(this);
      this.addEventListener('change', this.boundOnChange);
      this.addEventListener('click', this.boundOnClick);
      this.form?.addEventListener('submit', this.boundOnFormSubmit, true);
      document.addEventListener('click', this.boundOnDocumentClick);
      this.ensureSelection();
      this.update();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.boundOnFormSubmit, true);
      document.removeEventListener('click', this.boundOnDocumentClick);
    }

    parseVariants() {
      const script = this.querySelector('[data-cp-qb-variants]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error(error);
        return [];
      }
    }

    onClick(event) {
      const swatchButton = event.target.closest('[data-cp-qb-swatch-button]');
      if (swatchButton) {
        event.preventDefault();
        this.toggleSwatchDropdown(swatchButton);
        return;
      }

      const swatchOption = event.target.closest('[data-cp-qb-swatch-option]');
      if (swatchOption) {
        event.preventDefault();
        this.selectSwatchOption(swatchOption);
        return;
      }

      if (event.target.closest('select, button, a, input')) return;

      const card = event.target.closest('.cp-qb__container');
      if (!card) return;
      const deal = card.closest('[data-cp-qb-deal]');
      const radio = deal ? deal.querySelector('[data-cp-qb-radio]') : null;
      if (radio && !radio.disabled && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    onDocumentClick(event) {
      if (this.contains(event.target)) return;
      this.closeSwatchDropdowns();
    }

    onChange(event) {
      if (event.target.matches('[data-cp-qb-radio], [data-cp-qb-option-select]')) {
        if (!event.target.closest('[data-cp-qb-swatch-dropdown]')) this.closeSwatchDropdowns();
        this.update();
      }
    }

    onFormSubmit(event) {
      submitQuantityBreaks(event, this);
    }

    get activeDeal() {
      const checked = this.querySelector('[data-cp-qb-radio]:checked:not(:disabled)');
      return checked ? checked.closest('[data-cp-qb-deal]') : this.querySelector('[data-cp-qb-radio]:not(:disabled)')?.closest('[data-cp-qb-deal]');
    }

    ensureSelection() {
      const checked = this.querySelector('[data-cp-qb-radio]:checked:not(:disabled)');
      if (checked) return;
      const firstAvailable = this.querySelector('[data-cp-qb-radio]:not(:disabled)');
      if (firstAvailable) firstAvailable.checked = true;
    }

    toggleSwatchDropdown(button) {
      const dropdown = button.closest('[data-cp-qb-swatch-dropdown]');
      const menu = dropdown ? dropdown.querySelector('[data-cp-qb-swatch-menu]') : null;
      if (!dropdown || !menu) return;
      const willOpen = menu.hidden;
      this.closeSwatchDropdowns(dropdown);
      menu.hidden = !willOpen;
      dropdown.classList.toggle('is-open', willOpen);
      button.setAttribute('aria-expanded', String(willOpen));
    }

    closeSwatchDropdowns(except = null) {
      this.querySelectorAll('[data-cp-qb-swatch-dropdown]').forEach((dropdown) => {
        if (dropdown === except) return;
        dropdown.classList.remove('is-open');
        dropdown.querySelector('[data-cp-qb-swatch-menu]')?.setAttribute('hidden', '');
        dropdown.querySelector('[data-cp-qb-swatch-button]')?.setAttribute('aria-expanded', 'false');
      });
    }

    selectSwatchOption(option) {
      const dropdown = option.closest('[data-cp-qb-swatch-dropdown]');
      const select = dropdown ? dropdown.querySelector('[data-cp-qb-option-select]') : null;
      if (!select) return;
      select.value = option.dataset.value;
      this.update();
      this.closeSwatchDropdowns();
    }

    update() {
      this.ensureSelection();

      this.querySelectorAll('[data-cp-qb-deal]').forEach((deal) => {
        const radio = deal.querySelector('[data-cp-qb-radio]');
        deal.classList.toggle('is-selected', Boolean(radio && radio.checked && !radio.disabled));
        deal.classList.toggle('kaching-bundles__bar--selected', Boolean(radio && radio.checked && !radio.disabled));
      });

      const activeDeal = this.activeDeal;
      if (!activeDeal) return;

      const quantity = Number(activeDeal.dataset.quantity || 1);
      if (this.quantityInput) this.quantityInput.value = quantity;

      let firstVariantId = null;

      this.querySelectorAll('[data-cp-qb-deal]').forEach((deal) => {
        let hasInvalidSelection = false;

        deal.querySelectorAll('[data-cp-qb-row]').forEach((row) => {
          const variant = this.resolveVariant(row);
          if (!variant || variant.available === false) {
            row.dataset.variantId = '';
            hasInvalidSelection = true;
          } else {
            row.dataset.variantId = variant.id;
            if (deal === activeDeal && !firstVariantId) firstVariantId = variant.id;
            this.updateRowImage(row, variant);
          }

          this.updateOptionControls(row);
        });

        deal.classList.toggle('has-invalid-selection', hasInvalidSelection);
        this.updateDealPrice(deal);
      });

      if (firstVariantId && this.variantInput) {
        this.variantInput.value = firstVariantId;
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateOptionControls(row) {
      row.querySelectorAll('[data-cp-qb-option-wrap]').forEach((wrap) => {
        const select = wrap.querySelector('[data-cp-qb-option-select]');
        if (!select) return;

        const selectedSwatch = wrap.querySelector('[data-cp-qb-selected-swatch]');
        const selectedLabel = wrap.querySelector('[data-cp-qb-swatch-label]');
        const swatchTemplate = Array.from(wrap.querySelectorAll('[data-cp-qb-swatch-value]'))
          .find((swatch) => swatch.dataset.cpQbSwatchValue === select.value);

        if (selectedSwatch && swatchTemplate) selectedSwatch.innerHTML = swatchTemplate.innerHTML;
        if (selectedLabel) selectedLabel.textContent = select.value;

        wrap.querySelectorAll('[data-cp-qb-swatch-option]').forEach((option) => {
          option.setAttribute('aria-selected', String(option.dataset.value === select.value));
        });
      });
    }

    resolveVariant(row) {
      const options = Array.from(row.querySelectorAll('[data-cp-qb-option-select]'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((select) => select.value);

      return this.variants.find((variant) => {
        const variantOptions = variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean);
        return options.every((value, index) => variantOptions[index] === value);
      });
    }

    updateRowImage(row, variant) {
      const image = row.querySelector('[data-cp-qb-image]');
      if (!image) return;
      const nextSrc = this.getVariantImage(variant);
      if (nextSrc) image.src = nextSrc;
    }

    getVariantImage(variant) {
      const image = variant.featured_image || variant.featured_media || variant.image;
      if (!image) return '';
      if (typeof image === 'string') return image;
      return image.src || image.preview_image?.src || '';
    }

    updateDealPrice(deal) {
      const rows = Array.from(deal.querySelectorAll('[data-cp-qb-row]'));
      const quantity = Number(deal.dataset.quantity || 1);
      const fallbackVariant = this.variants.find((variant) => String(variant.id) === String(this.dataset.selectedVariantId)) || this.variants[0];
      const rowVariants = rows.length
        ? rows.map((row) => this.variants.find((variant) => String(variant.id) === String(row.dataset.variantId))).filter(Boolean)
        : Array.from({ length: quantity }, () => fallbackVariant).filter(Boolean);

      const regularTotal = rowVariants.reduce((total, variant) => total + Number(variant.price || 0), 0);
      const discountedTotal = this.applyDiscount(regularTotal, deal.dataset.discountType, deal.dataset.discountValue);
      const compareTotal = this.getCompareTotal(rowVariants, deal.dataset.compareSource);
      const priceDisplay = deal.dataset.priceDisplay || 'total';
      const unitLabel = deal.dataset.unitLabel || '';
      const priceElement = deal.querySelector('[data-cp-qb-price]');
      const compareElement = deal.querySelector('[data-cp-qb-compare]');
      const divisor = Math.max(rowVariants.length || quantity, 1);

      if (priceElement) {
        if (priceDisplay === 'unit') {
          priceElement.textContent = `${this.formatMoney(discountedTotal / divisor)} ${unitLabel}`.trim();
        } else {
          priceElement.textContent = this.formatMoney(discountedTotal);
        }
      }

      if (compareElement) {
        const shouldShowCompare = deal.dataset.compareSource !== 'none' && compareTotal > discountedTotal;
        compareElement.textContent = shouldShowCompare ? this.formatMoney(compareTotal) : '';
      }
    }

    applyDiscount(total, type, value) {
      const discountValue = this.parseSettingNumber(value);
      if (type === 'percentage') return Math.max(0, Math.round(total - total * (discountValue / 100)));
      if (type === 'fixed') return Math.max(0, Math.round(total - discountValue * 100));
      return Math.max(0, Math.round(total));
    }

    getCompareTotal(variants, source) {
      if (source === 'none') return 0;
      return variants.reduce((total, variant) => {
        const price = Number(variant.price || 0);
        const compareAt = Number(variant.compare_at_price || variant.compareAtPrice || variant.compareAt || 0);
        if (source === 'compare_at') return total + (compareAt > 0 ? compareAt : price);
        return total + price;
      }, 0);
    }

    parseSettingNumber(value) {
      const parsed = parseFloat(String(value || '0').replace(',', '.'));
      return Number.isFinite(parsed) ? parsed : 0;
    }

    formatMoney(cents) {
      const format = this.dataset.moneyFormat || '${{amount}}';
      const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
      const placeholder = this.getMoneyPlaceholder(placeholderMatch ? placeholderMatch[1] : 'amount');
      const value = this.formatWithDelimiters(cents, placeholder);
      return format.replace(/\{\{\s*\w+\s*\}\}/, value);
    }

    getMoneyPlaceholder(placeholder) {
      if (this.dataset.showDecimals !== 'none') return placeholder;
      if (placeholder.includes('comma')) return 'amount_no_decimals_with_comma_separator';
      if (placeholder.includes('apostrophe')) return 'amount_no_decimals_with_apostrophe_separator';
      return 'amount_no_decimals';
    }

    formatWithDelimiters(cents, placeholder) {
      const amount = Number(cents || 0) / 100;
      const noDecimals = placeholder.includes('no_decimals');
      const precision = noDecimals ? 0 : 2;
      const decimal = placeholder.includes('comma') ? ',' : '.';
      const thousands = placeholder.includes('comma') ? '.' : placeholder.includes('apostrophe') ? "'" : ',';
      const fixed = amount.toFixed(precision);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return precision ? parts.join(decimal) : parts[0];
    }

    getItems() {
      const activeDeal = this.activeDeal;
      if (!activeDeal || activeDeal.dataset.soldOut === 'true') return [];
      const items = [];
      const rows = activeDeal.querySelectorAll('[data-cp-qb-row]');

      rows.forEach((row) => {
        const variant = this.resolveVariant(row);
        const id = Number(variant?.id);
        if (!id) return;
        row.dataset.variantId = id;
        items.push({ id, quantity: 1 });
      });

      if (rows.length === 0 && this.variantInput) {
        items.push({ id: Number(this.variantInput.value), quantity: Number(activeDeal.dataset.quantity || 1) });
      }

      return items;
    }

    getExpectedItemCount() {
      const activeDeal = this.activeDeal;
      if (!activeDeal || activeDeal.dataset.soldOut === 'true') return 0;
      const rows = activeDeal.querySelectorAll('[data-cp-qb-row]');
      return rows.length || Number(activeDeal.dataset.quantity || 1);
    }
  }

  customElements.define('cp-quantity-breaks', CpQuantityBreaks);

  function submitQuantityBreaks(event, quantityBreaks = null) {
    if (event.defaultPrevented) return;
    const form = event.target;
    if (!form.matches('form[data-type="add-to-cart-form"]')) return;

    quantityBreaks = quantityBreaks || Array.from(document.querySelectorAll('cp-quantity-breaks'))
      .find((element) => element.dataset.formId === form.id);
    if (!quantityBreaks) return;

    quantityBreaks.update();
    const items = quantityBreaks.getItems();
    const expectedItemCount = quantityBreaks.getExpectedItemCount();
    const selectedItemCount = items.reduce((total, item) => total + item.quantity, 0);
    const hasInvalidSelection = !items.length || selectedItemCount !== expectedItemCount;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const productForm = form.closest('product-form');
    const submitButton = productForm ? productForm.querySelector('[type="submit"]') : form.querySelector('[type="submit"]');
    const spinner = productForm ? productForm.querySelector('.loading__spinner') : null;
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') return;
    if (productForm && typeof productForm.handleErrorMessage === 'function') productForm.handleErrorMessage();
    if (hasInvalidSelection) {
      if (productForm && typeof productForm.handleErrorMessage === 'function') {
        productForm.handleErrorMessage('Please choose available options for each item.');
      }
      return;
    }

    submitButton?.setAttribute('aria-disabled', 'true');
    submitButton?.classList.add('loading');
    spinner?.classList.remove('hidden');

    const config = typeof fetchConfig === 'function'
      ? fetchConfig('javascript')
      : { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' } };

    config.headers = config.headers || {};
    config.headers['Content-Type'] = 'application/json';
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    const body = { items };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }
    config.body = JSON.stringify(body);

    fetch(window.routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          if (productForm && typeof productForm.handleErrorMessage === 'function') {
            productForm.handleErrorMessage(response.description);
          }
          if (window.publish && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'cp-quantity-breaks',
              errors: response.errors || response.description,
              message: response.message,
            });
          }
          return;
        }

        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (window.publish && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cp-quantity-breaks',
            cartData: response,
          });
        }

        if (typeof cart.renderContents === 'function') cart.renderContents(response);
      })
      .catch((error) => console.error(error))
      .finally(() => {
        submitButton?.classList.remove('loading');
        submitButton?.removeAttribute('aria-disabled');
        spinner?.classList.add('hidden');
        if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      });
  }

  document.addEventListener(
    'submit',
    (event) => submitQuantityBreaks(event),
    true
  );
})();
